import React from "react";
import { Grid, Modal, Label, Message } from "semantic-ui-react";
import Separator from "components/Form/Separator";
import SecondaryButton from "components/Buttons/SecondaryButton";
import { isMobile } from "utils/isMobile";
import MainButton from "components/Buttons/MainButton";
import { Field, Form, Formik } from "formik";
import CustomInputTextComponent from "components/Form/CustomInputTextComponent";
import * as Yup from "yup";

const schemaValidation = Yup.object().shape({
	price: Yup.number().min(0.01, "Le prix doit être supérieur à 0").required("Champ requis"),
});

const styles = {
	mainContainer: {
		margin: 0,
		marginTop: -30,
	},
	column: {
		margin: "0.5em 0",
	},
	paid: {
		margin: "0.9em 0",
	},
	costColumnTitle: {
		color: "#007062",
		fontWeight: 550,
	},
	costColumnValue: {
		fontWeight: "Bold",
		textAlign: "right",
	},
	innerGrid: {
		marginBottom: 30,
	},
	costRow: {
		paddingBottom: 0,
		marginBottom: -25,
		marginLeft: 5,
		marginRight: 5,
		paddingLeft: 5,
		paddingRight: 5,
	},
	totalRow: {
		paddingTop: 0,
		marginTop: 25,
		backgroundColor: "#007062",
		borderRadius: 4,
		marginBottom: 20,
		marginLeft: 5,
		marginRight: 5,
		paddingLeft: 5,
		paddingRight: 5,
	},
	costColumnTotal: {
		color: "white",
	},
	costTitle: {
		textTransform: "uppercase",
		fontWeight: "Bold",
		color: "black",
	},
	nb: {
		fontStyle: "italic",
		fontSize: 12,
		marginTop: 35,
	},
};

const TotalCostModal = ({ open, close, folderCode, folderId, patientPhone, patientEmail, paid, prescriptions, costs }) => {
	const [paymentModalOpen, setPaymentModalOpen] = React.useState(false);
	const calculateCosts = () => {
		let totalCosts = {
			patientCosts: [],
			insuranceCosts: [],
			stateCosts: [],
			noCosts: [],
			companyCosts: [],
		};
		totalCosts.noCosts = prescriptions.flatMap((p) =>
			p.analyses.filter((a) => a.properties && !a.properties.calculating_cost).map((a) => a.properties.label)
		);
		if (costs && costs.length > 0 && costs.flatMap((c) => c.items).length > 0)
			costs
				.flatMap((c) => c.items)
				.forEach((analysis) => {
					if (analysis) {
						if (analysis.do_not_bill) totalCosts.stateCosts.push(analysis.title);
						else {
							if (analysis.price_patient + (analysis.price_patient_hn || 0) + (analysis.price_shipping || 0) > 0)
								totalCosts.patientCosts.push({
									title: analysis.title,
									price: analysis.price_patient + (analysis.price_patient_hn || 0) + (analysis.price_shipping || 0),
								});

							if (analysis.price_insurance > 0)
								totalCosts.insuranceCosts.push({
									title: analysis.title,
									price: analysis.price_insurance,
								});
							if (analysis.price_state > 0 && analysis.price_patient === 0) totalCosts.stateCosts.push(analysis.title);
							if (analysis.price_company > 0) totalCosts.companyCosts.push({ title: analysis.title, price: analysis.price_company });
						}
					}
				});
		if (costs && costs.length > 0 && costs.filter((c) => c.sampling_acts).length > 0) {
			costs
				.filter((c) => c.sampling_acts && c.bill_company)
				.flatMap((c) => c.sampling_acts)
				.forEach((act) => totalCosts.companyCosts.push({ title: act.title, price: act.price }));
			costs
				.filter((c) => c.sampling_acts && !c.bill_company)
				.flatMap((c) => c.sampling_acts)

				.forEach((act) => totalCosts.patientCosts.push({ title: act.title, price: act.price }));
		}
		totalCosts.patientCosts = totalCosts.patientCosts.reduce((acc, act) => {
			!acc.some((a) => a.title === act.title) ? acc.push(act) : (acc.find((a) => a.title === act.title).price += act.price);
			return acc;
		}, []);
		return totalCosts;
	};

	const getTotalCost = (costs) => {
		return costs.reduce((total, cost) => (total += cost.price), 0);
	};

	const sumUpPayment = (price) => {
		window.open(
			`sumupmerchant://pay/1.0?affiliate-key=${process.env.REACT_APP_SUMUP_AFFILIATE_KEY}&app-id=${process.env.REACT_APP_SUMUP_APP_ID}&total=${price}&currency=${process.env.REACT_APP_SUMUP_CURRENCY}&title=${folderCode}&receipt-mobilephone=${patientPhone}&receipt-email=${patientEmail}&callback=${process.env.REACT_APP_SUMUP_CALLBACK_BASE_URL}${folderId}/${price}/${folderCode}`
		);
		setPaymentModalOpen(false);
		close();
	};

	const getPaymentsFolderBySource = () => {
		let partielSum = [];
		paid.payments &&
			paid.payments
				.filter((p) => !p.invoice_reference)
				.forEach((payment) => {
					if (
						partielSum.length > 0 &&
						partielSum.find(
							(ps) => ps.source === payment.source && ps.invoice_reference === payment.invoice_reference && ps.voucher_code === payment.voucher_code
						)
					) {
						partielSum.find(
							(ps) => ps.source === payment.source && ps.invoice_reference === payment.invoice_reference && ps.voucher_code === payment.voucher_code
						).price += payment.price;
					} else
						partielSum.push({
							source: payment.source,
							price: payment.price,
							voucher_code: payment.voucher_code,
							invoice_reference: payment.invoice_reference,
						});
				});
		return partielSum;
	};

	const { patientCosts, insuranceCosts, stateCosts, noCosts, companyCosts } = calculateCosts();

	const patientTotalCost = getTotalCost(patientCosts).toFixed(2);

	return (
		<Modal open={open} onClose={close} closeOnEscape closeOnDimmerClick>
			<Modal.Header>Prise en charge des analyses</Modal.Header>
			<Message>
				<Message.Header>MYLAB est connecté avec DKV.</Message.Header>
				<Message.Content>
					Pensez, en cas de reste à charge, à renseigner dans le profil patient sa mutuelle. Le calcul des coûts à la charge du patient intégrera
					automatiquement les frais pris éventuellement en charge par la DKV en fonction de son contrat.
					<br /> Une analyse peut être prise en charge partiellement ou à 100% par DKV. Dans le cas d’une prise en charge partielle, l’analyse
					apparaitra à la fois en « A charge du patient » et « A charge de l’assurance ».
				</Message.Content>
			</Message>
			<Modal.Content>
				<Grid style={styles.mainContainer}>
					<Grid.Row columns={1}>
						<Grid.Column style={styles.column}>
							{companyCosts && companyCosts.length > 0 && (
								<div style={styles.column}>
									<span style={styles.costTitle}>à charge de la société </span>
									<Separator />
									<Grid style={styles.innerGrid}>
										{companyCosts.map((cost, i) => {
											return (
												<Grid.Row columns={2} style={styles.costRow} key={`patient${i}`}>
													<Grid.Column
														style={{
															...styles.column,
															...styles.costColumnTitle,
														}}
														mobile={10}
														tablet={8}
														computer={8}
													>
														{cost.title}
													</Grid.Column>
													<Grid.Column
														style={{
															...styles.column,
															...styles.costColumnValue,
														}}
														mobile={6}
														tablet={8}
														computer={8}
													>
														{cost.price.toFixed(2)} €
													</Grid.Column>
												</Grid.Row>
											);
										})}
									</Grid>
								</div>
							)}
							<div style={styles.column}>
								<span style={styles.costTitle}>
									à charge du patient{" "}
									{paid && paid.payments && paid.payments.length > 0 && <span style={{ ...styles.costTitle, color: "red" }}>*</span>}
								</span>
								<Separator />
								<Grid>
									{patientCosts.map((cost, i) => {
										return (
											<Grid.Row columns={2} style={styles.costRow} key={`patient${i}`}>
												<Grid.Column
													style={{
														...styles.column,
														...styles.costColumnTitle,
													}}
													mobile={10}
													tablet={8}
													computer={8}
												>
													{cost.title}
												</Grid.Column>
												<Grid.Column
													style={{
														...styles.column,
														...styles.costColumnValue,
													}}
													mobile={6}
													tablet={8}
													computer={8}
												>
													{cost.price.toFixed(2)} €
												</Grid.Column>
											</Grid.Row>
										);
									})}
								</Grid>
								<Grid>
									<Grid.Row columns={2} style={{ ...styles.costRow, ...styles.totalRow }}>
										<Grid.Column style={{ ...styles.column, ...styles.costColumnTotal }} mobile={10} tablet={8} computer={8}>
											Total
										</Grid.Column>
										<Grid.Column
											style={{
												...styles.column,
												...styles.costColumnTotal,
												...styles.costColumnValue,
											}}
											mobile={6}
											tablet={8}
											computer={8}
										>
											{patientTotalCost} €
										</Grid.Column>
									</Grid.Row>
								</Grid>
							</div>
							{insuranceCosts.length > 0 && (
								<div style={styles.column}>
									<span style={styles.costTitle}>
										{costs.insurance_name ? `à la charge de ${costs.insurance_name} - n° ${costs.insurance_number}` : "à charge de l'assurance (DKV)"}
									</span>
									<Separator />
									<Grid>
										{insuranceCosts.map((cost, i) => {
											return (
												<Grid.Row columns={2} style={styles.costRow} key={`insurance${i}`}>
													<Grid.Column
														style={{
															...styles.column,
															...styles.costColumnTitle,
														}}
														mobile={10}
														tablet={8}
														computer={8}
													>
														{cost.title}
													</Grid.Column>
													<Grid.Column
														style={{
															...styles.column,
															...styles.costColumnValue,
														}}
														mobile={6}
														tablet={8}
														computer={8}
													>
														{cost.price.toFixed(2)} €
													</Grid.Column>
												</Grid.Row>
											);
										})}
									</Grid>
									<Grid>
										<Grid.Row columns={2} style={{ ...styles.costRow, ...styles.totalRow }}>
											<Grid.Column style={{ ...styles.column, ...styles.costColumnTotal }} mobile={10} tablet={8} computer={8}>
												Total
											</Grid.Column>
											<Grid.Column
												style={{
													...styles.column,
													...styles.costColumnTotal,
													...styles.costColumnValue,
												}}
												mobile={6}
												tablet={8}
												computer={8}
											>
												{getTotalCost(insuranceCosts).toFixed(2)} €
											</Grid.Column>
										</Grid.Row>
									</Grid>
								</div>
							)}

							{stateCosts.length > 0 && (
								<Grid.Row columns={1}>
									<Grid.Column style={styles.column}>
										<span style={styles.costTitle}>Remboursées</span>
										<Separator />
										<Grid>
											{stateCosts &&
												stateCosts.map((cost, i) => {
													return (
														<Grid.Row columns={1} style={styles.costRow} key={`state${i}`}>
															<Grid.Column
																style={{
																	...styles.column,
																	...styles.costColumnTitle,
																}}
																mobile={16}
																tablet={16}
																computer={16}
															>
																{cost}
															</Grid.Column>
														</Grid.Row>
													);
												})}
										</Grid>
									</Grid.Column>
								</Grid.Row>
							)}

							{noCosts && noCosts.length > 0 && (
								<div style={styles.column}>
									<span style={styles.costTitle}>
										Coût non calculé - une facture peut être envoyée ultérieurement pour les analyses suivantes :{" "}
										{paid && paid.payments && paid.payments.length > 0 && <span style={{ ...styles.costTitle, color: "red" }}>*</span>}
									</span>
									<Separator />
									<Grid>
										{noCosts.map((cost, i) => {
											return (
												<Grid.Row columns={1} style={styles.costRow} key={`no-cost${i}`}>
													<Grid.Column
														style={{
															...styles.column,
															...styles.costColumnTitle,
														}}
														mobile={16}
														tablet={16}
														computer={16}
													>
														{cost}
													</Grid.Column>
												</Grid.Row>
											);
										})}
									</Grid>
								</div>
							)}
						</Grid.Column>
						{patientTotalCost === 0 && (
							<Grid.Column style={{ ...styles.column, ...styles.nb }}>
								<Message>
									<Message.Header>
										En l’absence de montant à la charge du patient (total = 0€), une information est donnée au patient en lui indiquant qu’une facture
										peut être émise en fonction des analyses complémentaires à réaliser.
									</Message.Header>
								</Message>
							</Grid.Column>
						)}
						{paid && paid.payments && paid.payments.length > 0 && (
							<Grid.Column style={{ ...styles.paid, color: "red", marginTop: "40px" }}>
								* Encaissement pour ce dossier{" "}
								{getPaymentsFolderBySource()?.map((p, index) => (
									<Label key={`payment-${index}`}>
										{p.price.toFixed(2)} €{" "}
										{p.source === "toBill"
											? "via Envoi facture"
											: p.source === "stripe"
											? "via un paiement en ligne"
											: `(paiement ${p.source === "especes" ? "en espèces" : p.source})`}
									</Label>
								))}
							</Grid.Column>
						)}
					</Grid.Row>
				</Grid>
			</Modal.Content>
			<Modal.Actions>
				{/* <MainButton
					onClick={() => setPaymentModalOpen(true)}
					style={{ margin: isMobile() ? '-14px .25em 5px 0' : '-0 .25em 0 0' }}
					disabled={!paymentEnabled}
				>
					Payer avec SumUp
				</MainButton> */}
				<SecondaryButton onClick={close} style={{ margin: isMobile() ? "-14px .25em 5px 0" : "-0 .25em 0 0" }}>
					Fermer
				</SecondaryButton>
			</Modal.Actions>
			<Formik
				initialValues={{
					price: patientTotalCost,
				}}
				validationSchema={schemaValidation}
				onSubmit={(values) => {
					sumUpPayment(values.price.toFixed(2));
				}}
			>
				{({ submitForm }) => {
					return (
						<Form>
							<Modal onClose={() => setPaymentModalOpen(false)} open={paymentModalOpen} size="small">
								<Modal.Header>Montant à payer</Modal.Header>
								<Modal.Content>
									<Field
										type="number"
										name="price"
										customlabel="Veuillez saisir le montant à payer en euro"
										placeholder=""
										component={CustomInputTextComponent}
									/>
								</Modal.Content>
								<Modal.Actions>
									<MainButton text="Valider" onClick={submitForm} />
									<SecondaryButton onClick={() => setPaymentModalOpen(false)}>Annuler</SecondaryButton>
								</Modal.Actions>
							</Modal>
						</Form>
					);
				}}
			</Formik>
		</Modal>
	);
};

export default TotalCostModal;
